// proofOfWork.js — solves the server's ALTCHA challenge before an anonymous generation.
//
// Invisible by design: no widget, no puzzle, no images. The browser brute-forces a small number
// and the user never sees it. That is the whole point — a hosted bot check (reCAPTCHA,
// Turnstile) would put a third-party script in the path, which is blocked in China and brings a
// data processor and a consent question with it. This runs entirely between our own page and
// our own server.
//
// It costs the user well under a second and costs a scripted attacker the same per request,
// which is what turns a free three-HTTP-call loop into something with a real price.
//
// Fetches the challenge directly rather than through apiService: apiService imports this
// module, so going back through it would be a circular import. No loss — the challenge endpoint
// is unauthenticated, so none of apiService's token handling applies.
import { API_BASE_URL } from "../config/runtime";

const encoder = new TextEncoder();

async function sha256Hex(input) {
  const digest = await crypto.subtle.digest("SHA-256", encoder.encode(input));
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/**
 * Find the number the server hid, by trying each one until the hash matches.
 *
 * Yields to the event loop periodically. Without that, a slow phone freezes visibly while
 * solving, which would defeat the point of an invisible check.
 */
async function solve(challenge, salt, maxNumber) {
  for (let n = 0; n <= maxNumber; n++) {
    if ((await sha256Hex(`${salt}${n}`)) === challenge) return n;
    if (n % 2000 === 0 && n > 0) {
      await new Promise((resolve) => setTimeout(resolve, 0));
    }
  }
  return null;
}

/**
 * Fetch a challenge, solve it, and return the proof to send as X-Altcha-Proof.
 *
 * Returns null on any failure rather than throwing. The server side ships in monitor-only and
 * is designed to fail open, so a proof we could not produce must never be the reason a real
 * person cannot make a video — an old browser without crypto.subtle, say.
 */
export async function getProofOfWork() {
  try {
    if (!crypto?.subtle) return null;

    const response = await fetch(`${API_BASE_URL}/api/video/pow-challenge`);
    if (!response.ok) return null;
    const { challenge, salt, signature, maxnumber, algorithm } = await response.json();
    if (!challenge || !salt || !signature) return null;

    const number = await solve(challenge, salt, maxnumber ?? 50000);
    if (number === null) return null;

    // Base64 of the payload the server re-derives and checks. It re-hashes salt+number itself,
    // so a forged payload fails; the signature proves the challenge was one we issued.
    return btoa(
      JSON.stringify({ algorithm: algorithm || "SHA-256", challenge, number, salt, signature }),
    );
  } catch (err) {
    console.error("Proof of work failed:", err);
    return null;
  }
}
